import type { UIMessage } from "ai";
import { getLiveBackendResult } from "./live-chat";

/** Relay public answer text once, including across tool-approval continuations. */
export function createLiveTextRelay(emit: (text: string) => void) {
  const offsets = new Map<string, number>();
  let emitted = false;
  const update = (message: UIMessage | undefined, final = false) => {
    if (message?.role !== "assistant") return;
    message.parts.forEach((part, index) => {
      if (part.type !== "text") return;
      const key = `${message.id}:${index}`;
      const offset = offsets.get(key) ?? 0;
      const remaining = part.text.slice(offset);
      // Wait for a sentence boundary plus whitespace, or a completed text part.
      // This avoids speaking half a number, URL, or streamed word. Markdown/code
      // blocks are held until complete so their fragments aren't read aloud.
      let length = 0;
      if (final || part.state === "done") length = remaining.length;
      else if (!remaining.includes("```")) {
        for (const match of remaining.matchAll(/[.!?。！？]["'”’)]*(?:\s+|(?=[\p{Script=Han}]))/gu)) {
          const end = match.index + match[0].length;
          // Common titles/abbreviations are not useful standalone sentences.
          if (!/\b(?:Mr|Mrs|Ms|Dr|Prof|St|e\.g|i\.e)\.\s*$/i.test(remaining.slice(0, end))) length = end;
        }
      }
      if (!length) return;
      offsets.set(key, offset + length);
      const text = remaining.slice(0, length).trim();
      if (text) { emitted = true; emit(text); }
    });
  };
  return {
    update,
    finish(message?: UIMessage) {
      update(message, true);
      // A tool may finish with only a card after a spoken preamble. Include its
      // safe result even though earlier steps already emitted some text.
      const lastStep = message?.parts.findLastIndex((part) => part.type === "step-start") ?? -1;
      const lastTool = message?.parts.findLastIndex((part) => part.type === "dynamic-tool" || part.type.startsWith("tool-")) ?? -1;
      const hasAnswerAfterTools = message?.parts.slice(Math.max(lastStep, lastTool) + 1)
        .some((part) => part.type === "text" && part.text.trim());
      if (!emitted || (lastTool >= 0 && !hasAnswerAfterTools)) {
        emit(getLiveBackendResult(message ? { ...message, parts: message.parts.filter((part) => part.type !== "text") } : undefined));
      }
    },
  };
}

/** Content-free milestones, visible in browser devtools for real voice runs. */
export function createLiveTiming(id: string) {
  const started = performance.now();
  const seen = new Set<string>();
  return (stage: string) => {
    if (seen.has(stage)) return;
    seen.add(stage);
    console.info("[LiveChat timing]", { id, stage, elapsedMs: Math.round(performance.now() - started) });
  };
}

export type LiveDelegateObserver = {
  onText: (text: string) => void;
  onFirstText: () => void;
};
